import {API_URL} from "../settings/local_settings";
import {APIClient} from "./APIClient";
import {LocalStorage} from "./LocalStorage";
import {ScreenTracker} from "./ScreenTracker";

// Links de la app al Centro de Ayuda con QUIÉN los abre y DESDE DÓNDE.
//
// El sitio de ayuda (ayuda.dashbo.io) no puede leer la sesión de la app (vive en el
// localStorage de otro origen). Entonces la app le pide al Backend un token firmado que
// sólo sirve para atribuir visitas (`GET help-center/visitor-token/`) y lo pega en el link
// como `hc_id`, junto con la pantalla actual como `hc_from`. El sitio los lee, los saca de
// la URL y los manda con la visita. Con eso el Command Center muestra qué usuario leyó cada
// artículo y qué pantallas de la app mandan más gente a la ayuda.
//
// Nunca bloquea la navegación: si el token todavía no llegó, el link sale sin él.

const HELP_CENTER_HOSTS = ['ayuda.dashbo.io'];
// El token vence a los 7 días en el Backend; se renueva mucho antes.
const REFRESH_AFTER_MS = 24 * 60 * 60 * 1000;
// Tras un fallo no se reintenta por un rato: cada link a la ayuda llama a prefetch al
// montarse, y sin esto un endpoint caído se pedía (y se reportaba a Sentry) una vez por link.
const RETRY_AFTER_FAILURE_MS = 5 * 60 * 1000;

let _token = null;
let _fetchedAt = 0;
let _forSession = null;   // el token es de ESTA sesión: otro login lo invalida
let _inflight = null;
let _failedAt = 0;

function _isFresh() {
    return _token !== null
        && _forSession === LocalStorage.fetchUserToken()
        && Date.now() - _fetchedAt < REFRESH_AFTER_MS;
}

// Pide el token si hace falta. Se llama al montar un link a la ayuda, así el token ya está
// cuando el usuario hace clic.
export function prefetchHelpCenterIdentity() {
    if (_isFresh() || _inflight !== null || !LocalStorage.fetchUserToken()) {
        return;
    }
    if (_failedAt && Date.now() - _failedAt < RETRY_AFTER_FAILURE_MS) {
        return;
    }
    const session = LocalStorage.fetchUserToken();
    _inflight = new APIClient(`${API_URL}help-center/visitor-token/`).call('GET')
        .then((response) => {
            if (response && response.status === 'OK' && response.token) {
                _token = response.token;
                _fetchedAt = Date.now();
                _forSession = session;
                _failedAt = 0;
            } else {
                _failedAt = Date.now();
            }
        })
        .catch(() => {
            _failedAt = Date.now();
        })
        .finally(() => {
            _inflight = null;
        });
}

// Devuelve la URL con `hc_id` y `hc_from` si es un link al Centro de Ayuda; cualquier otra
// URL (relativa, otro dominio) vuelve tal cual. `source` pisa la pantalla actual como origen:
// lo usa el chat de soporte, que flota sobre cualquier pantalla y no es ninguna de ellas.
export function withHelpCenterAttribution(url, source = null) {
    let parsed;
    try {
        parsed = new URL(url);
    } catch (e) {
        return url;
    }
    if (!HELP_CENTER_HOSTS.includes(parsed.hostname)) {
        return url;
    }
    if (_isFresh()) {
        parsed.searchParams.set('hc_id', _token);
    }
    const screen = source || ScreenTracker.currentScreen();
    if (screen) {
        parsed.searchParams.set('hc_from', screen);
    }
    return parsed.toString();
}

// Handler de `onClick` para un `<a target="_blank">` a la ayuda: reescribe el href justo
// antes de que el navegador lo abra (el render no puede esperar al token). Usado directo como
// handler (`onClick={attributeHelpCenterClick}`) React no pasa `source`: va la pantalla actual.
export function attributeHelpCenterClick(event, source = null) {
    const anchor = event.currentTarget;
    if (anchor && anchor.href) {
        anchor.href = withHelpCenterAttribution(anchor.getAttribute('data-help-url') || anchor.href, source);
    }
}

// Sólo para tests.
export function _resetHelpCenterAttributionForTests() {
    _token = null;
    _fetchedAt = 0;
    _forSession = null;
    _inflight = null;
    _failedAt = 0;
}
